import React from 'react';
import { Building, MapPin, Utensils, Shield, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import ServicePageLayout from './ServicePageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export default function TravelAccommodation() {
  const { t } = useTranslation();
  
  return (
    <ServicePageLayout
      title={t('services.travelAccommodation.title')}
      subtitle={t('services.travelAccommodation.subtitle')}
      icon={Building}
      heroColor="from-amber-500 to-orange-600"
      enquiryTitle={t('services.travelAccommodation.title')}
    >
      {/* Main Description */}
      <Card className="mb-8 shadow-lg" data-testid="service-main-card">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('services.travelAccommodation.mainTitle')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            {t('services.travelAccommodation.mainDesc1')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('services.travelAccommodation.mainDesc2')}
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-proximity">
          <CardContent className="p-6">
            <MapPin className="w-12 h-12 text-amber-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.travelAccommodation.features.proximity.title')}</h3>
            <p className="text-gray-700">
              {t('services.travelAccommodation.features.proximity.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-amenities">
          <CardContent className="p-6">
            <Building className="w-12 h-12 text-amber-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.travelAccommodation.features.amenities.title')}</h3>
            <p className="text-gray-700">
              {t('services.travelAccommodation.features.amenities.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-meals">
          <CardContent className="p-6">
            <Utensils className="w-12 h-12 text-amber-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.travelAccommodation.features.meals.title')}</h3>
            <p className="text-gray-700">
              {t('services.travelAccommodation.features.meals.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-safety">
          <CardContent className="p-6">
            <Shield className="w-12 h-12 text-amber-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.travelAccommodation.features.safety.title')}</h3>
            <p className="text-gray-700">
              {t('services.travelAccommodation.features.safety.desc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Services Included */}
      <Card className="mb-8 shadow-lg" data-testid="services-included-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.travelAccommodation.servicesTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t('services.travelAccommodation.servicesList').map((service) => (
              <div key={service} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{service}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Accommodation Types */}
      <Card className="mb-8 shadow-lg" data-testid="accommodation-types-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.travelAccommodation.typesTitle')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.travelAccommodation.types.budget.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.travelAccommodation.types.budget.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.travelAccommodation.types.budget.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.travelAccommodation.types.comfort.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.travelAccommodation.types.comfort.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.travelAccommodation.types.comfort.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.travelAccommodation.types.luxury.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.travelAccommodation.types.luxury.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.travelAccommodation.types.luxury.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </ServicePageLayout>
  );
}
